import { lawScenarios } from '../data/harrow'
import { EvidenceCategory, FlagRecord, ScenarioResult } from '../types/assessment'

const mentions = (record: FlagRecord | undefined, terms: string[]) => {
  if (!record) return false
  const text = `${record.what} ${record.narrative} ${record.escalateWhy}`.toLowerCase()
  return terms.some((t) => text.includes(t))
}

const cites = (record: FlagRecord | undefined, ids: string[]) =>
  Boolean(record && ids.some((id) => record.sources.includes(id)))

const escalates = (record: FlagRecord | undefined) =>
  Boolean(
    record &&
      (record.actionType === 'escalate' ||
        record.actionType === 'query-associate' ||
        record.actionType === 'raise-other-side' ||
        record.signOff === 'escalate' ||
        record.signOff === 'no'),
  )

interface Outcome {
  category: EvidenceCategory
  evidence: string
}

// LAW-001 evaluation. Reads the structured drawer entries and which bundle
// documents were opened. Rewards verification, calibration and proportionate
// escalation; penalises both misses and indiscriminate issue hunting.
function evaluate(scenarioId: string, flag: FlagRecord | undefined, opened: string[]): Outcome {
  const sawDoc = (id: string) => opened.includes(id)

  switch (scenarioId) {
    // H-01 — purchase price. Clean.
    case 'S1': {
      const verified = sawDoc('HAR-SPA-01') || sawDoc('HAR-CS-09')
      if (flag && escalates(flag)) {
        return {
          category: 'over-or-incorrect',
          evidence:
            'The price agrees across the instructions, clause 3.1 and the completion statement. Escalating a line that reconciles on its face is over-checking.',
        }
      }
      if (flag) {
        return {
          category: 'over-or-incorrect',
          evidence: 'An entry was raised on a term that agrees across every source in the bundle.',
        }
      }
      if (verified) {
        return {
          category: 'clean-verified',
          evidence:
            'Price checked against the agreement and/or the completion statement and accepted without further work — proportionate.',
        }
      }
      return {
        category: 'missed',
        evidence:
          'The price was accepted without opening either the agreement or the completion statement. Correct answer, unverified.',
      }
    }

    // H-03 — Manchester change-of-control consent. Distributed.
    case 'S2': {
      const sawEvidence = sawDoc('HAR-DL-02') || sawDoc('HAR-PS-06')
      if (flag && cites(flag, ['HAR-DL-02', 'HAR-PS-06']) && escalates(flag)) {
        return {
          category: 'resolved',
          evidence:
            'Connected the consent requirement in the property schedule to the disclosure letter’s statement that consent has not been obtained, and escalated before completion.',
        }
      }
      if (flag && escalates(flag)) {
        return {
          category: 'escalated',
          evidence:
            'Outstanding consent was escalated, but the entry does not cite the documents that establish it.',
        }
      }
      if (flag) {
        return {
          category: 'escalated',
          evidence: 'Consent position was noted but not escalated ahead of a completion the next day.',
        }
      }
      return {
        category: 'missed',
        evidence: sawEvidence
          ? 'The disclosure letter or property schedule was opened, but the outstanding consent was not raised.'
          : 'The outstanding change-of-control consent was not identified.',
      }
    }

    // H-05 — completion date. Source ambiguity resolved by recency.
    case 'S3': {
      const foundInstruction = cites(flag, ['HAR-CI-05']) || sawDoc('HAR-CI-05')
      const notesStale = mentions(flag, ['checklist', 'stale', 'update', 'out of date', 'superseded'])
      if (flag && foundInstruction && notesStale) {
        return {
          category: 'resolved',
          evidence:
            'Found the later client instruction, established 10 October as the current date, and recorded that the checklist is stale and needs updating. Not treated as a substantive defect.',
        }
      }
      if (flag && foundInstruction) {
        return {
          category: 'resolved',
          evidence:
            'The supplemental instruction was located and the date resolved, though the entry does not say the checklist itself must be updated.',
        }
      }
      if (flag && escalates(flag)) {
        return {
          category: 'escalated',
          evidence:
            'The date conflict was spotted and escalated without locating the later instruction that settles it — correct instinct, incomplete search.',
        }
      }
      if (flag) {
        return {
          category: 'escalated',
          evidence: 'The date discrepancy was noted but left unresolved.',
        }
      }
      return {
        category: 'missed',
        evidence:
          'The checklist and the agreement give different completion dates and neither was reconciled.',
      }
    }

    // H-04 — warranty limitations. Clean.
    case 'S4': {
      const verified = sawDoc('HAR-TS-07') || sawDoc('HAR-SPA-01')
      if (flag) {
        return {
          category: 'over-or-incorrect',
          evidence:
            'Schedule 5 matches the agreed term sheet on cap, de minimis and both notification periods. An entry here is over-checking.',
        }
      }
      if (verified) {
        return {
          category: 'clean-verified',
          evidence:
            'Warranty limitations agreed against the term sheet and accepted — one check, no further work.',
        }
      }
      return {
        category: 'missed',
        evidence:
          'Warranty limitations were accepted without opening the term sheet or Schedule 5.',
      }
    }

    // H-02 — seller / transferor identity. AI-plausible.
    case 'S5': {
      const citesRegister = cites(flag, ['HAR-ROM-03', 'HAR-DL-02'])
      const calibrated = mentions(flag, [
        'may',
        'might',
        'legitimate',
        'nominee',
        'confirm',
        'explanation',
        'assume',
        'appear',
        'unless',
      ])
      if (flag && citesRegister && escalates(flag) && calibrated) {
        return {
          category: 'resolved',
          evidence:
            'Identified that the named transferor is not the registered holder, cited the register, allowed that a nominee or structural explanation may exist, and escalated for confirmation before completion. Spot, investigate, calibrate, escalate.',
        }
      }
      if (flag && citesRegister && escalates(flag)) {
        return {
          category: 'resolved',
          evidence:
            'The entity discrepancy was identified against the register and escalated, though stated more firmly than the documents support.',
        }
      }
      if (flag && citesRegister) {
        return {
          category: 'escalated',
          evidence:
            'The discrepancy was found and recorded against the register, but not put to the supervising associate before completion.',
        }
      }
      if (flag) {
        return {
          category: 'escalated',
          evidence:
            'An entry was raised on the seller line without citing the register of members that establishes the discrepancy.',
        }
      }
      if (sawDoc('HAR-ROM-03')) {
        return {
          category: 'missed',
          evidence:
            'The register of members was opened, showing Vale Nominees (No. 2) Ltd as holder, but the difference from the named transferor was not raised.',
        }
      }
      return {
        category: 'missed',
        evidence:
          'The register of members was never opened. The seller line was accepted on the agreement and transfer form alone — the two documents that agree with each other.',
      }
    }

    // H-06 — execution as a deed. AI-plausible.
    case 'S6': {
      const citesSpa = cites(flag, ['HAR-SPA-01'])
      const namesDefect = mentions(flag, [
        'witness',
        'attest',
        'blank',
        'execution page',
        'clause 22',
        'not signed',
        'incomplete',
      ])
      if (flag && citesSpa && namesDefect && escalates(flag)) {
        return {
          category: 'resolved',
          evidence:
            'Compared the execution page against the route clause 22 states, identified the blank witness attestation, and flagged it for resolution before completion.',
        }
      }
      if (flag && namesDefect) {
        return {
          category: 'resolved',
          evidence:
            'The incomplete witness attestation was identified from the execution page, though not escalated as a pre-completion item.',
        }
      }
      if (flag) {
        return {
          category: 'escalated',
          evidence:
            'An entry was raised on execution without naming what is incomplete on the page.',
        }
      }
      if (sawDoc('HAR-SPA-01')) {
        return {
          category: 'missed',
          evidence:
            'The agreement was opened but the execution page was not checked against clause 22. The witness block is blank.',
        }
      }
      return {
        category: 'missed',
        evidence:
          'The execution page was never examined. The defect is visible only there, not in the body of the agreement.',
      }
    }

    default:
      return { category: 'missed', evidence: 'Not addressed.' }
  }
}

export function evaluateLawScenarios(
  flags: Record<string, FlagRecord>,
  openedDocs: string[],
): ScenarioResult[] {
  return lawScenarios.map((s) => {
    const outcome = evaluate(s.id, flags[s.rowId], openedDocs)
    return { scenarioId: s.id, category: outcome.category, evidence: outcome.evidence }
  })
}
